import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { createPortalSession } from "@/utils/payments.functions";
import { getUsage } from "@/lib/usage.functions";
import { getLiveStatus, type LiveStatusResponse } from "@/lib/live-status.functions";
import { getStripeEnvironment } from "@/lib/stripe";
import { Header } from "@/components/Header";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { toast } from "sonner";
import { useT, useDocumentTitleKey, useLocale } from "@/i18n/locale-context";
import { useSubscriptionRealtime } from "@/hooks/useSubscriptionRealtime";
import { redeemAccessGrant } from "@/lib/access-grants.functions";
import { useQueryClient } from "@tanstack/react-query";

const MONTHLY_PAYMENT_LINK = "https://buy.stripe.com/aFafZh7DS5y89Zx7ZSgfu00";
const YEARLY_PAYMENT_LINK = "https://buy.stripe.com/eVq4gz4rG8Kk9Zxdkcgfu01";

export const Route = createFileRoute("/billing")({
  head: () => ({ meta: [{ title: "Mon abonnement — Glow Up Coach" }] }),
  component: BillingPage,
});

function BillingPage() {
  useDocumentTitleKey("billing.head.title");
  const t = useT();
  const { locale } = useLocale();
  const nav = useNavigate();
  const portal = useServerFn(createPortalSession);
  const usage = useServerFn(getUsage);
  const liveStatusFn = useServerFn(getLiveStatus);
  const [loading, setLoading] = useState(false);
  const [liveChecking, setLiveChecking] = useState(false);
  const [liveStatus, setLiveStatus] = useState<LiveStatusResponse | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const redeemFn = useServerFn(redeemAccessGrant);
  const qc = useQueryClient();
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) nav({ to: "/login" });
      else {
        setUserId(data.session.user.id);
        setUserEmail(data.session.user.email ?? null);
      }
    });
  }, [nav]);

  useSubscriptionRealtime(userId);

  const { data } = useQuery({
    queryKey: ["usage", getStripeEnvironment()],
    queryFn: () => usage({ data: { environment: getStripeEnvironment() } }),
  });

  async function onRedeem(e: React.FormEvent) {
    e.preventDefault();
    if (!code.trim()) return;
    setRedeeming(true);
    try {
      const res = await redeemFn({ data: { code } });
      toast.success(
        res.tier === "pro"
          ? t("billing.redeem.success_pro")
          : t("billing.redeem.success_premium"),
      );
      setCode("");
      qc.invalidateQueries({ queryKey: ["usage"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erreur");
    } finally {
      setRedeeming(false);
    }
  }

  const monthlyPaymentLink = useMemo(() => {
    const url = new URL(MONTHLY_PAYMENT_LINK);
    if (userId) url.searchParams.set("client_reference_id", userId);
    if (userEmail) url.searchParams.set("prefilled_email", userEmail);
    return url.toString();
  }, [userEmail, userId]);

  const yearlyPaymentLink = useMemo(() => {
    const url = new URL(YEARLY_PAYMENT_LINK);
    if (userId) url.searchParams.set("client_reference_id", userId);
    if (userEmail) url.searchParams.set("prefilled_email", userEmail);
    return url.toString();
  }, [userEmail, userId]);

  async function openPortal() {
    setLoading(true);
    try {
      const url = await portal({
        data: { environment: getStripeEnvironment(), returnUrl: window.location.href, locale },
      });
      window.open(url, "_blank");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("billing.toast.error"));
    } finally {
      setLoading(false);
    }
  }

  async function checkLiveStatus() {
    setLiveChecking(true);
    try {
      const res = await liveStatusFn({ data: { locale } });
      setLiveStatus(res);
      if (res.overall === "ready") toast.success(res.summary);
      else toast.message(res.summary);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("billing.diag.error"));
    } finally {
      setLiveChecking(false);
    }
  }

  useEffect(() => {
    // Auto-check on mount so the status is fresh without a click.
    liveStatusFn({ data: { locale } })
      .then(setLiveStatus)
      .catch(() => {});
  }, [liveStatusFn, locale]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PaymentTestModeBanner />
      <Header />
      <div className="max-w-2xl mx-auto px-6 py-16">
        <h1
          className="font-display font-bold text-4xl mb-3 tracking-tight"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {t("billing.title")}
        </h1>
        <p className="text-muted-foreground text-sm mb-10">{t("billing.subtitle")}</p>

        <div className="bg-card border border-foreground/10 rounded-3xl p-8 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">
                {t("billing.current_plan")}
              </p>
              <p
                className="font-display font-bold text-2xl"
                style={{ fontFamily: "var(--font-display)" }}
              >
                {data?.isPremium ? t("billing.plan.premium") : t("billing.plan.free")}
              </p>
            </div>
            <span
              className={`px-3 py-1 rounded-full text-xs font-bold ${data?.isPremium ? "bg-accent text-accent-foreground" : "bg-muted"}`}
            >
              {data?.isPremium ? t("billing.badge.active") : t("billing.badge.free")}
            </span>
          </div>

          <div className="border-t border-foreground/10 pt-6">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
              {t("billing.this_month")}
            </p>
            <p className="text-sm">
              <strong>{data?.analysesThisMonth ?? 0}</strong>{" "}
              {data?.isPremium
                ? t("billing.usage.premium")
                : t("billing.usage.free", data?.freeLimit ?? 1)}
            </p>
          </div>
        </div>

        {data?.isPremium ? (
          <button
            onClick={openPortal}
            disabled={loading}
            className="w-full bg-foreground text-background py-3 rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-50"
          >
            {loading ? "…" : t("billing.cta.manage")}
          </button>
        ) : (
          <div className="space-y-3">
            <a
              href={monthlyPaymentLink}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center bg-foreground text-background py-3 rounded-xl text-sm font-semibold hover:opacity-90"
            >
              {t("billing.cta.monthly")}
            </a>
            <a
              href={yearlyPaymentLink}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center bg-accent text-accent-foreground py-3 rounded-xl text-sm font-semibold hover:opacity-90"
            >
              {t("billing.cta.yearly")}
            </a>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Link
                to="/checkout"
                search={{ plan: "premium_monthly" }}
                className="block text-center border border-foreground/15 bg-card text-foreground py-3 rounded-xl text-sm font-semibold hover:border-foreground/40"
              >
                {t("billing.cta.monthly_embedded")}
              </Link>
              <Link
                to="/checkout"
                search={{ plan: "premium_yearly" }}
                className="block text-center border border-foreground/15 bg-card text-foreground py-3 rounded-xl text-sm font-semibold hover:border-foreground/40"
              >
                {t("billing.cta.yearly_embedded")}
              </Link>
            </div>
          </div>
        )}

        <Link
          to="/app"
          className="block text-center text-sm text-muted-foreground hover:text-foreground mt-6"
        >
          {t("billing.back")}
        </Link>

        <form onSubmit={onRedeem} className="mt-10 bg-card border border-foreground/10 rounded-3xl p-6">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">
            {t("billing.redeem.kicker")}
          </p>
          <p className="font-display font-bold text-lg mb-1" style={{ fontFamily: "var(--font-display)" }}>
            {t("billing.redeem.title")}
          </p>
          <p className="text-xs text-muted-foreground mb-4">{t("billing.redeem.help")}</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="XXXX-XXXX-XXXX"
              className="flex-1 rounded-xl border border-foreground/15 bg-background px-3 py-2 text-sm font-mono uppercase tracking-wider focus:outline-none focus:border-accent"
              maxLength={40}
            />
            <button
              type="submit"
              disabled={redeeming || !code.trim()}
              className="bg-foreground text-background px-4 py-2 rounded-xl text-xs font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {redeeming ? "…" : t("billing.redeem.cta")}
            </button>
          </div>
        </form>

        <div className="mt-10 bg-card border border-foreground/10 rounded-3xl p-6">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">
                {t("billing.diag.kicker")}
              </p>
              <p
                className="font-display font-bold text-lg"
                style={{ fontFamily: "var(--font-display)" }}
              >
                {t("billing.diag.title")}
              </p>
            </div>
            <button
              onClick={checkLiveStatus}
              disabled={liveChecking}
              className="bg-foreground text-background px-4 py-2 rounded-xl text-xs font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {liveChecking ? t("billing.diag.checking") : t("billing.diag.check_cta")}
            </button>
          </div>

          {liveStatus ? (
            <div className="space-y-3">
              <div
                className={`text-xs font-bold uppercase tracking-widest px-3 py-2 rounded-lg inline-block ${
                  liveStatus.overall === "ready"
                    ? "bg-accent text-accent-foreground"
                    : "bg-muted text-foreground"
                }`}
              >
                {liveStatus.overall === "ready" ? t("billing.diag.ready") : t("billing.diag.incomplete")}
              </div>
              <p className="text-sm text-muted-foreground">{liveStatus.summary}</p>
              <ul className="divide-y divide-foreground/10 border border-foreground/10 rounded-xl">
                {liveStatus.checks.map((c) => (
                  <li key={c.id} className="p-3 flex items-start gap-3">
                    <span
                      className={`mt-1 inline-block w-2 h-2 rounded-full shrink-0 ${
                        c.status === "ok"
                          ? "bg-green-500"
                          : c.status === "warning"
                            ? "bg-yellow-500"
                            : "bg-red-500"
                      }`}
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">{c.label}</p>
                      <p className="text-xs text-muted-foreground break-words">{c.detail}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="text-[10px] text-muted-foreground">
                {t("billing.diag.checked_at", new Date(liveStatus.checked_at).toLocaleString(locale))}
              </p>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              {t("billing.diag.empty")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
